import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  getConsent,
  setConsent,
  loadMetaPixel,
  CONSENT_ACCEPTED,
  CONSENT_REJECTED,
} from '../lib/cookieConsent';

/**
 * GDPR-compliant cookie banner.
 * Appears on the first visit until the user makes a choice.
 * Uses localStorage so it never nags the same visitor twice.
 */
const CookieConsent = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (existing === CONSENT_ACCEPTED) {
      loadMetaPixel();
    } else if (!existing) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    setConsent(CONSENT_ACCEPTED);
    loadMetaPixel();
    setVisible(false);
  };

  const handleReject = () => {
    setConsent(CONSENT_REJECTED);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] px-4 pb-4 pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-white/95 backdrop-blur-md border border-gray-200 shadow-2xl rounded-2xl p-5 sm:p-6 pointer-events-auto">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
              {t('Cookies bij U&Fashion', 'Cookies at U&Fashion', 'Cookies chez U&Fashion')}
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {t(
                'Wij gebruiken essentiële cookies om de website goed te laten werken. Met jouw toestemming plaatsen we daarnaast analytische cookies (Meta Pixel) om te meten welke advertenties en pagina\u2019s werken. Zo kunnen we U&Fashion beter maken.',
                'We use essential cookies to make the website work. With your consent we also place analytics cookies (Meta Pixel) to measure which ads and pages perform well. This helps us improve U&Fashion.',
                'Nous utilisons des cookies essentiels pour le bon fonctionnement du site. Avec votre accord, nous ajoutons des cookies d\u2019analyse (Meta Pixel) pour mesurer nos publicités et pages. Cela nous permet d\u2019améliorer U&Fashion.'
              )}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              <Link to="/privacy" className="underline hover:text-pink-600" data-testid="cookie-consent-privacy-link">
                {t('Lees ons privacybeleid', 'Read our privacy policy', 'Lire notre politique de confidentialité')}
              </Link>
            </p>
          </div>
          <button
            onClick={handleReject}
            className="text-gray-400 hover:text-gray-600 shrink-0"
            aria-label={t('Sluiten en weigeren', 'Close and reject', 'Fermer et refuser')}
            data-testid="cookie-consent-close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-5">
          <button
            onClick={handleReject}
            className="flex-1 px-5 py-2.5 rounded-full border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
            data-testid="cookie-consent-reject"
          >
            {t('Alleen noodzakelijk', 'Only essential', 'Uniquement nécessaires')}
          </button>
          <button
            onClick={handleAccept}
            className="flex-1 px-5 py-2.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-semibold hover:from-pink-600 hover:to-purple-600 transition-colors"
            data-testid="cookie-consent-accept"
          >
            {t('Alles accepteren', 'Accept all', 'Tout accepter')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
